// src/components/SalidaEntradaPatrones/OtSelector.jsx
import React, { useState, useEffect } from 'react';
import { Form, Badge, Spinner, ListGroup, InputGroup } from 'react-bootstrap';
import { FaSearch, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { filtroOTTexto, getDataList } from '../../api/common';
import moment from 'moment';

/**
 * Componente selector de Órdenes de Trabajo con búsqueda
 */
const OtSelector = ({
  value = [],
  onChange,
  multiple = true,
  disabled = false,
  filterByStatus = null,
  otDefault = null, 
  OnlyOne = false  // Si true, solo permite seleccionar una OT (comportamiento tipo dropdown clásico) o colocar en disable el buscador si ya hay una OT seleccionada
}) => {
  const [searchText, setSearchText] = useState('');
  const [otsOptions, setOtsOptions] = useState([]);
  const [selectedOts, setSelectedOts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [defaultOtData, setDefaultOtData] = useState(null);


  // Cargar OT por defecto
  useEffect(() => {
    if (otDefault) {
      setDefaultOtData(otDefault);
      // Agregar la OT por defecto a selectedOts si no está
      setSelectedOts(prev => {
        const exists = prev.some(ot => ot._id === otDefault._id);
        if (!exists) {
          const newSelectedOts = [...prev, otDefault];
          // Si la OT por defecto no está en value, agregarla
          if (!value.includes(otDefault._id)) {
            onChange([...value, otDefault._id], newSelectedOts);
          }
          return newSelectedOts;
        }
        return prev;
      });
    }
  }, [otDefault]);

  // Cargar OTs seleccionadas solo al inicio (cuando value viene del padre por primera vez)
  useEffect(() => {
    if (value && value.length > 0 && selectedOts.length === 0) {
      loadSelectedOts(value);
    }
  }, []);

  // Buscar OTs cuando cambia el texto de búsqueda
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchText.trim().length >= 2) {
        handleSearch();
      } else if (searchText.trim().length === 0) {
        loadInitialOts();
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const loadInitialOts = async () => {
    setLoading(true);
    try {

        // si no hay texto que no busque nada
      if(!searchText || searchText.trim() === '') {
        setOtsOptions([]);
        return;
      }
      const response = await filtroOTTexto({searchText: ''});
      setOtsOptions(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar OTs:', error);
      setOtsOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const conditions = {
        $or: [
          { numeroOrden: { $regex: searchText, $options: 'i' } },
          { 'cliente.nombre': { $regex: searchText, $options: 'i' } }
        ],
        estado: { $in: ['Pendiente', 'En proceso', 'En ejecución'] }
      };

      if (filterByStatus) {
        conditions.estado = filterByStatus;
      }

      const response = await filtroOTTexto({searchText});

      setOtsOptions(response.data.data || []);
    } catch (error) {
      console.error('Error al buscar OTs:', error);
      setOtsOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedOts = async (otIds) => {
    try {
      // Primero intentar obtener de otsOptions
      const fromOptions = otsOptions.filter(ot => otIds.includes(ot._id));
      
      // Si no están todas, cargar desde el servidor
      const missingIds = otIds.filter(id => !fromOptions.some(ot => ot._id === id));
      
      if (missingIds.length > 0) {
        // Cargar las OTs faltantes desde el servidor
        const promises = missingIds.map(async (id) => {
          try {
            const response = await getDataList('ordenes', {
              conditions: { _id: id }
            });
            return response.data.data?.[0];
          } catch (error) {
            console.error(`Error al cargar OT ${id}:`, error);
            return null;
          }
        });
        
        const loadedOts = await Promise.all(promises);
        const validOts = loadedOts.filter(ot => ot !== null);
        
        setSelectedOts([...fromOptions, ...validOts]);
      } else {
        setSelectedOts(fromOptions);
      }
    } catch (error) {
      console.error('Error al cargar OTs seleccionadas:', error);
    }
  };

  const handleSelectOt = (ot, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (disabled) return;

    let newValue;
    let newSelectedOts;
    if (multiple) {
      const isSelected = value.includes(ot._id);
      if (isSelected) {
        newValue = value.filter(id => id !== ot._id);
        newSelectedOts = selectedOts.filter(o => o._id !== ot._id);
      } else {
        newValue = [...value, ot._id];
        newSelectedOts = [...selectedOts, ot];
      }
      setSelectedOts(newSelectedOts);
    } else {
      newValue = [ot._id];
      newSelectedOts = [ot];
      setSelectedOts(newSelectedOts);
      setShowDropdown(false);
    }

    // Enviar tanto los IDs como los objetos completos
    onChange(newValue, newSelectedOts);
    setSearchText('');
  };

  const handleRemoveOt = (otId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (disabled) return;
    // No permitir eliminar la OT por defecto
    if (defaultOtData && otId === defaultOtData._id) {
      return;
    }
    
    // Actualizar ambos estados de manera sincronizada
    const newValue = value.filter(id => id !== otId);
    const newSelectedOts = selectedOts.filter(o => o._id !== otId);
    
    setSelectedOts(newSelectedOts);
    // Enviar tanto los IDs como los objetos completos
    onChange(newValue, newSelectedOts);
  };

  const isDefaultOt = (otId) => {
    return defaultOtData && otId === defaultOtData._id;
  };

  const isOtSelected = (otId) => {
    return value.includes(otId);
  };

  const getEstadoBadge = (estado) => {
    const estadosConfig = {
      'Pendiente': 'warning',
      'En proceso': 'info',
      'En ejecución': 'primary',
      'Completada': 'success',
      'Cancelada': 'danger'
    };
    return estadosConfig[estado] || 'secondary';
  };

  return (
    <div className="ot-selector">
      {/* OTs seleccionadas (chips) */}
      {(selectedOts.length > 0 || defaultOtData) && (
        <div className="mb-2 d-flex flex-wrap gap-2">
          {/* Mostrar OT por defecto primero si existe */}
          {defaultOtData && (
            <Badge
              key={defaultOtData._id}
              bg="primary"
              className="d-flex align-items-center gap-2 p-2"
              style={{ fontSize: '0.9rem' }}
              title="OT de origen - No se puede eliminar"
            >
              <span>
                📌 <strong>{defaultOtData.numeroOrden}</strong> - {defaultOtData.cliente || 'Sin cliente'}
              </span>
              <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                Origen
              </Badge>
            </Badge>
          )}
          {/* Mostrar otras OTs seleccionadas */}
          {selectedOts.filter(ot => !isDefaultOt(ot._id)).map(ot => (
            <Badge
              key={ot._id}
              bg="info"
              className="d-flex align-items-center gap-2 p-2"
              style={{ fontSize: '0.9rem' }}
            >
              <span>
                <strong>{ot.numeroOrden}</strong> - {ot.cliente?.nombre || 'Sin cliente'}
              </span>
              {!disabled && (
                <FaTimes
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => handleRemoveOt(ot._id, e)}
                />
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Si OnlyOne es true y ya hay una OT seleccionada, deshabilitar el buscador */}
      {/* Buscador */}
      {OnlyOne && value.length > 0 ? (
        <div className="text-muted mb-2">Solo se permite seleccionar una OT. Para cambiarla, primero elimine la OT seleccionada.</div>
      ) : null}
      { !OnlyOne || (OnlyOne && value.length === 0) ? (
        <InputGroup className="mb-2">
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
          type="text"
          placeholder="N° Orden, Cliente o Ciudad..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => {
            setShowDropdown(true);
            if (otsOptions.length === 0) {
              loadInitialOts();
            }
          }}
          disabled={disabled}
        />
        {loading && (
          <InputGroup.Text>
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>
      ) : null}

      {/* Dropdown de opciones */}
      {showDropdown && otsOptions.length > 0 && (
        <ListGroup
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            position: 'relative',
            zIndex: 1000
          }}
        >
          {otsOptions.map(ot => {
            const selected = isOtSelected(ot._id);
            return (
              <ListGroup.Item
                key={ot._id}
                action
                active={selected}
                onClick={(e) => handleSelectOt(ot, e)}
                disabled={disabled}
                className="d-flex justify-content-between align-items-center"
              >
                <div className="flex-grow-1">
                  <div>
                    <strong>{ot.numeroOrden}</strong>
                  </div>
                  <small className="text-muted">
                    Cliente: {ot.cliente?.nombre  || 'Sin cliente'} - ({ot.cliente?.ciudad })
                    {ot.fechaEstimadaEntrega && (
                      <> | Entrega: {moment(ot.fechaEstimadaEntrega).format('DD/MM/YYYY')}</>
                    )}
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg={getEstadoBadge(ot.estado)} style={{ fontSize: '0.75rem' }}>
                    {ot.estado}
                  </Badge>
                  {selected && <FaCheckCircle className="text-success" />}
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {/* Sin resultados */}
      {showDropdown && !loading && otsOptions.length === 0 && searchText.trim().length > 0 && (
        <div className="text-center text-muted p-3 border rounded">
          No se encontraron órdenes de trabajo
        </div>
      )}
    </div>
  );
};

export default OtSelector;
